// Step3AccountHolder.jsx
//
// Wizard step 3: account holder legal name, service address, and notification
// emails. The per-number `account_holder_name` and `service_address` fields
// are the #1 source of port rejections. The values must exactly match the
// losing carrier's bill of record, so we coach this hard.
//
// Google Places autocomplete is wired in when `REACT_APP_GOOGLE_PLACES_API_KEY`
// is set. Otherwise a manual 5-field address form ships. The two render the
// same shape into `service_address`.
//
// Common-case shortcut: a "use the same details for every number" toggle. Most
// SMBs have all numbers on one bill, so letting them fill once and propagate
// avoids repetitive typing. Per-number edits are still available afterward.

import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Mail, User2, MapPin, X } from 'lucide-react';
import {
  isConfigured as placesIsConfigured,
  loadGooglePlaces,
  placeResultToServiceAddress,
} from '../../../../../../lib/googlePlaces';

function formatForDisplay(e164) {
  if (!e164 || e164.length < 12) return e164;
  const d = e164.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) {
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  return e164;
}

const EMPTY_ADDR = { street1: '', street2: '', city: '', state: '', zip: '', google_place_id: null };

export default function Step3AccountHolder({ draftState }) {
  const { draft, upsertNumber, patchTopLevel } = draftState;
  const [sameForAll, setSameForAll] = useState(true);
  const [emailDraft, setEmailDraft] = useState('');

  if (!draft) return null;

  const portable = (draft.numbers ?? []).filter((n) => n?.portability?.portable === true);
  const first = portable[0];

  const applyAccountHolder = (name) => {
    if (sameForAll) {
      for (const n of portable) {
        upsertNumber(n.phone_number, { account_holder_name: name });
      }
    } else if (first) {
      upsertNumber(first.phone_number, { account_holder_name: name });
    }
  };

  const applyAddress = (addr) => {
    if (sameForAll) {
      for (const n of portable) {
        upsertNumber(n.phone_number, { service_address: addr });
      }
    } else if (first) {
      upsertNumber(first.phone_number, { service_address: addr });
    }
  };

  const addEmail = () => {
    const e = emailDraft.trim();
    if (!e) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return;
    const existing = Array.isArray(draft.notification_emails) ? draft.notification_emails : [];
    if (existing.includes(e)) {
      setEmailDraft('');
      return;
    }
    patchTopLevel({ notification_emails: [...existing, e] });
    setEmailDraft('');
  };

  const removeEmail = (idx) => {
    const existing = Array.isArray(draft.notification_emails) ? draft.notification_emails : [];
    patchTopLevel({ notification_emails: existing.filter((_, i) => i !== idx) });
  };

  return (
    <div>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5 text-xs text-amber-900 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold mb-0.5">Match your carrier bill exactly</div>
          The account holder name and service address must match the bill on
          file with your current carrier, character for character.
          <span className="font-medium"> John Smith vs John A. Smith</span> is
          the most common reason ports get rejected.
        </div>
      </div>

      <h3 className="text-base font-semibold text-slate-900 mb-1">
        Account holder and service address
      </h3>
      <p className="text-xs text-slate-500 mb-4">
        Tell us who owns the line on the carrier's records and where the
        service is registered.
      </p>

      {/* Phase D: Twilio losing_carrier_information.customer_type */}
      <div className="mb-5">
        <label className="text-xs font-medium text-slate-700 mb-1.5 block">
          Is the carrier account registered to a business or an individual?
        </label>
        <p className="text-[11px] text-slate-500 mb-2">
          Use the same designation your carrier has on the bill. The losing
          carrier rejects ports when this disagrees with their records.
        </p>
        <div className="flex gap-2">
          {['Business', 'Individual'].map((opt) => {
            const active = draft.losing_carrier_customer_type === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => patchTopLevel({ losing_carrier_customer_type: opt })}
                className={`flex-1 px-4 py-2.5 text-sm rounded-lg border transition-colors ${
                  active
                    ? 'border-orange-500 bg-orange-50 text-orange-900 font-medium'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                }`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>

      {portable.length > 1 && (
        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={sameForAll}
            onChange={(e) => setSameForAll(e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
          />
          <span className="text-sm text-slate-700">
            Use the same account holder and address for all {portable.length} numbers
          </span>
        </label>
      )}

      {sameForAll && first ? (
        <AccountHolderForm
          accountHolderName={first.account_holder_name ?? ''}
          serviceAddress={first.service_address ?? EMPTY_ADDR}
          onAccountHolderChange={applyAccountHolder}
          onAddressChange={applyAddress}
        />
      ) : (
        <div className="space-y-5">
          {portable.map((entry) => (
            <div key={entry.phone_number} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs font-medium text-slate-500 mb-3">
                {formatForDisplay(entry.phone_number)}
              </div>
              <AccountHolderForm
                accountHolderName={entry.account_holder_name ?? ''}
                serviceAddress={entry.service_address ?? EMPTY_ADDR}
                onAccountHolderChange={(name) =>
                  upsertNumber(entry.phone_number, { account_holder_name: name })
                }
                onAddressChange={(addr) =>
                  upsertNumber(entry.phone_number, { service_address: addr })
                }
              />
            </div>
          ))}
        </div>
      )}

      {/* Notification emails */}
      <div className="mt-6 pt-6 border-t border-slate-100">
        <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700 mb-1.5">
          <Mail className="w-4 h-4 text-slate-400" />
          Notify these emails as the port progresses
        </label>
        <p className="text-xs text-slate-500 mb-3">
          We'll send updates when the carrier accepts, schedules, or completes
          the port.
        </p>
        <div className="flex gap-2 mb-2">
          <input
            type="email"
            value={emailDraft}
            onChange={(e) => setEmailDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addEmail();
              }
            }}
            placeholder="name@company.com"
            className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
          <button
            type="button"
            onClick={addEmail}
            className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors"
          >
            Add
          </button>
        </div>
        {Array.isArray(draft.notification_emails) && draft.notification_emails.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {draft.notification_emails.map((email, idx) => (
              <span
                key={`${email}-${idx}`}
                className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs px-2 py-1 rounded-md"
              >
                {email}
                <button
                  type="button"
                  onClick={() => removeEmail(idx)}
                  className="text-slate-400 hover:text-red-600"
                  aria-label={`Remove ${email}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-500">
        Bill upload and Letter of Authorization e-signature land in upcoming
        releases. Your draft will pick up exactly where you left off.
      </div>
    </div>
  );
}

function AccountHolderForm({
  accountHolderName,
  serviceAddress,
  onAccountHolderChange,
  onAddressChange,
}) {
  return (
    <div className="space-y-4">
      <div>
        <label className="flex items-center gap-1.5 text-xs font-medium text-slate-700 mb-1.5">
          <User2 className="w-3.5 h-3.5 text-slate-400" />
          Account holder legal name
        </label>
        <input
          type="text"
          value={accountHolderName}
          onChange={(e) => onAccountHolderChange(e.target.value)}
          placeholder="ACME Inc. or John A. Smith"
          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
        />
      </div>

      <ServiceAddressInput value={serviceAddress} onChange={onAddressChange} />
    </div>
  );
}

function ServiceAddressInput({ value, onChange }) {
  const configured = placesIsConfigured();
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [places, setPlaces] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [showManual, setShowManual] = useState(!configured);

  useEffect(() => {
    if (!configured || showManual) return;
    let cancelled = false;
    loadGooglePlaces()
      .then((google) => {
        if (cancelled) return;
        setPlaces(google);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError(true);
        setShowManual(true);
      });
    return () => {
      cancelled = true;
    };
  }, [configured, showManual]);

  useEffect(() => {
    if (!places || !inputRef.current || autocompleteRef.current) return;
    try {
      const ac = new places.maps.places.Autocomplete(inputRef.current, {
        types: ['address'],
        componentRestrictions: { country: ['us'] },
        fields: ['address_components', 'place_id', 'formatted_address'],
      });
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        const addr = placeResultToServiceAddress(place);
        if (addr) onChange(addr);
      });
      autocompleteRef.current = ac;
    } catch {
      setLoadError(true);
      setShowManual(true);
    }
  }, [places, onChange]);

  const updateField = (field, val) => onChange({ ...value, [field]: val });

  return (
    <div>
      <label className="flex items-center gap-1.5 text-xs font-medium text-slate-700 mb-1.5">
        <MapPin className="w-3.5 h-3.5 text-slate-400" />
        Service address
        {!configured && (
          <span className="text-[10px] font-normal text-slate-400">(manual entry)</span>
        )}
      </label>

      {!showManual ? (
        <>
          <input
            ref={inputRef}
            type="text"
            defaultValue={value?.street1 ? formatAddressOneLine(value) : ''}
            placeholder="Start typing your service address"
            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            autoComplete="off"
          />
          {loadError && (
            <p className="text-[11px] text-amber-700 mt-1">
              Address autocomplete is unavailable right now.
              <button
                type="button"
                onClick={() => setShowManual(true)}
                className="ml-1 underline hover:no-underline"
              >
                Enter address manually
              </button>
            </p>
          )}
          {value?.street1 && (
            <div className="mt-2 grid grid-cols-1 gap-2 text-xs text-slate-600">
              <div>
                {value.street1}
                {value.street2 ? `, ${value.street2}` : ''}
              </div>
              <div>
                {value.city}, {value.state} {value.zip}
              </div>
              <button
                type="button"
                onClick={() => setShowManual(true)}
                className="text-xs text-orange-600 hover:text-orange-700 self-start"
              >
                Edit fields manually
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input
            type="text"
            value={value?.street1 ?? ''}
            onChange={(e) => updateField('street1', e.target.value)}
            placeholder="Street address"
            className="sm:col-span-2 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
          <input
            type="text"
            value={value?.street2 ?? ''}
            onChange={(e) => updateField('street2', e.target.value)}
            placeholder="Suite, unit, or floor (optional)"
            className="sm:col-span-2 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
          <input
            type="text"
            value={value?.city ?? ''}
            onChange={(e) => updateField('city', e.target.value)}
            placeholder="City"
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              value={value?.state ?? ''}
              onChange={(e) => updateField('state', e.target.value.toUpperCase().slice(0, 2))}
              placeholder="State"
              maxLength={2}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent uppercase"
            />
            <input
              type="text"
              inputMode="numeric"
              value={value?.zip ?? ''}
              onChange={(e) => updateField('zip', e.target.value)}
              placeholder="ZIP"
              maxLength={10}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function formatAddressOneLine(addr) {
  if (!addr) return '';
  const parts = [
    addr.street1,
    addr.street2,
    addr.city,
    addr.state ? `${addr.state} ${addr.zip ?? ''}`.trim() : addr.zip ?? '',
  ].filter(Boolean);
  return parts.join(', ');
}
